import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  ScatterChart, Scatter, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Person as PersonIcon,
  EventBusy as EventBusyIcon,
  Insights as InsightsIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, TeacherImpactData } from '../../services/learningAnalyticsApi';

const TeacherImpactTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TeacherImpactData | null>(null);
  const [year, setYear] = useState<string>('');
  const [district, setDistrict] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (district) params.district = district;

      const response = await learningAnalyticsAPI.getTeacherImpact(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load teacher impact data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, district]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, schools, correlation_data, monthly_trend } = data;

  // Unique districts from schools for filter dropdown
  const districts = Array.from(new Set(schools.map(s => s.district_name))).sort();

  // Determine correlation strength label
  const getCorrelationLabel = (r: number): string => {
    const abs = Math.abs(r);
    if (abs >= 0.7) return 'Strong';
    if (abs >= 0.4) return 'Moderate';
    if (abs >= 0.2) return 'Weak';
    return 'Very Weak';
  };

  const getCorrelationColor = (r: number): 'success' | 'warning' | 'error' | 'default' => {
    const abs = Math.abs(r);
    if (abs >= 0.7) return 'success';
    if (abs >= 0.4) return 'warning';
    return 'default';
  };

  // Custom tooltip for scatter chart
  const ScatterTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="body2" fontWeight="bold">{d.school_name}</Typography>
          <Typography variant="caption" display="block">
            Teacher Rate: {d.teacher_rate.toFixed(1)}%
          </Typography>
          <Typography variant="caption" display="block">
            Student Rate: {d.student_rate.toFixed(1)}%
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Year" value={year}
              onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="">Current</MenuItem>
              {[2026, 2025, 2024, 2023, 2022].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}>
              <MenuItem value="">All Districts</MenuItem>
              {districts.map(d => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon color="primary" />
                <Typography variant="body2" color="textSecondary">Avg Teacher Attendance</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {summary.avg_teacher_attendance.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <EventBusyIcon color="error" />
                <Typography variant="body2" color="textSecondary">Learning Days Lost</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {summary.total_learning_days_lost.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <InsightsIcon sx={{ color: '#f57c00' }} />
                <Typography variant="body2" color="textSecondary">Correlation Coefficient</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {summary.correlation_coefficient.toFixed(3)}
              </Typography>
              <Chip
                label={getCorrelationLabel(summary.correlation_coefficient)}
                size="small"
                color={getCorrelationColor(summary.correlation_coefficient)}
                sx={{ mt: 0.5 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WarningIcon color="error" />
                <Typography variant="body2" color="textSecondary">Worst School</Typography>
              </Box>
              <Typography variant="body1" fontWeight="bold" sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {summary.worst_school || 'N/A'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Lowest teacher attendance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Teacher vs Student Attendance Scatter Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Teacher vs Student Attendance</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Each dot represents a school. Higher correlation = teacher attendance strongly impacts students.
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="teacher_rate"
                    name="Teacher Rate"
                    unit="%"
                    domain={[0, 100]}
                    label={{ value: 'Teacher Attendance %', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="student_rate"
                    name="Student Rate"
                    unit="%"
                    domain={[0, 100]}
                    label={{ value: 'Student Attendance %', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<ScatterTooltip />} />
                  <Scatter
                    data={correlation_data}
                    fill="#1976d2"
                    fillOpacity={0.6}
                    name="Schools"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Trend Line Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Monthly Teacher Attendance Trend</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Average teacher attendance rate by month.
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="attendance_rate"
                    stroke="#1976d2"
                    strokeWidth={3}
                    name="Attendance Rate %"
                    dot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schools Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>School Teacher Impact Details</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Teacher attendance and its estimated impact on student learning days.
          </Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>School</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell align="right">Teacher Count</TableCell>
                  <TableCell align="right">Teacher Att %</TableCell>
                  <TableCell align="right">Absent Days</TableCell>
                  <TableCell align="right">Learning Days Lost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schools.map((school) => (
                  <TableRow key={school.school_reg_number} hover>
                    <TableCell>{school.school_name}</TableCell>
                    <TableCell>{school.district_name}</TableCell>
                    <TableCell align="right">{school.teacher_count}</TableCell>
                    <TableCell align="right"
                      sx={{ color: school.teacher_attendance_rate >= 80 ? '#388e3c' : '#d32f2f' }}>
                      {school.teacher_attendance_rate.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">{school.absent_teacher_days.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Typography
                        fontWeight="bold"
                        sx={{ color: school.learning_days_lost > 0 ? '#d32f2f' : '#388e3c' }}
                      >
                        {school.learning_days_lost.toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TeacherImpactTab;
